import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useGroup } from '../context/GroupContext';
import authService from '../api/authService';
import config from '../config';
import PaymentLinksModal from '../components/modals/PaymentLinksModal';
import AdminLayout from '../components/AdminLayout';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faCamera, 
  faSave, 
  faLock, 
  faSignOutAlt, 
  faCheck,
  faArrowLeft,
  faUser,
  faCreditCard
} from '@fortawesome/free-solid-svg-icons';
import '../styles/AdminProfile.css';

const AdminProfilePage = () => {
  const { currentUser, logout } = useAuth();
  const { adminGroup, loading: groupLoading, updateAdminGroup } = useGroup();
  const navigate = useNavigate();
  const location = useLocation();
  const [saving, setSaving] = useState(false);
  // Add profile state initialization
  const [profile, setProfile] = useState(currentUser || {});
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    isPublic: true, // Default to true if no privacy setting exists yet
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
    // Payment links for CharityBox
    paymentLinks: {
      paypal: '',
      zelle: '',
      venmo: '',
      cashapp: '',
      other: ''
    }
  });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [coverFile, setCoverFile] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [activeTab, setActiveTab] = useState('account'); // Default to Account tab
  const [showPaymentLinksModal, setShowPaymentLinksModal] = useState(false);
  const fileInputRef = useRef(null);
  const coverFileInputRef = useRef(null);

  // Initialize form data when adminGroup is loaded
  useEffect(() => {
    if (adminGroup) {
      setFormData(prevState => ({
        ...prevState,
        name: adminGroup.name || '',
        description: adminGroup.description || '',
        isPublic: adminGroup.is_public !== false, // Default to true if undefined
        paymentLinks: {
          paypal: adminGroup.payment_links?.paypal || '',
          zelle: adminGroup.payment_links?.zelle || '',
          venmo: adminGroup.payment_links?.venmo || '',
          cashapp: adminGroup.payment_links?.cashapp || '',
          other: adminGroup.payment_links?.other || ''
        }
      }));
      
      // Set logo preview if available
      if (adminGroup.logo_url) {
        setLogoPreview(adminGroup.logo_url);
      }
      
      // Set cover image preview if available
      if (adminGroup.cover_image) {
        setCoverPreview(adminGroup.cover_image);
      }
    }
  }, [adminGroup]);

  // Initialize profile when currentUser changes
  useEffect(() => {
    if (currentUser) {
      setProfile(currentUser);
    }
  }, [currentUser]);

  // Handle URL parameters for tab switching
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const tabParam = searchParams.get('tab');
    if (tabParam && ['account', 'password'].includes(tabParam)) {
      setActiveTab(tabParam);
    }
  }, [location.search]);

  // Fetch latest profile data from database to ensure stripe_account_id is current
  useEffect(() => {
    const fetchProfileData = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;

        const response = await fetch(`${config.API_URL}/users/profile`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          const result = await response.json();
          if (result.success && result.data) {
            setProfile(prevProfile => ({
              ...prevProfile,
              ...result.data,
              stripe_account_id: result.data.stripe_account_id
            }));
          }
        }
      } catch (error) {
        console.error('Error fetching profile data:', error);
      }
    };

    fetchProfileData();
  }, []);

  // Handle input change
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Handle checkbox inputs differently
    const inputValue = type === 'checkbox' ? checked : value;
    
    // Handle nested payment links
    if (name.startsWith('paymentLinks.')) {
      const paymentField = name.split('.')[1];
      setFormData(prevState => ({
        ...prevState,
        paymentLinks: {
          ...prevState.paymentLinks,
          [paymentField]: inputValue
        }
      }));
    } else {
      setFormData(prevState => ({
        ...prevState,
        [name]: inputValue
      }));
    }
  };

  // Handle logo file input change
  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      
      // Create a preview URL
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Trigger the file input click when clicking on the logo area
  const handleLogoClick = () => {
    fileInputRef.current.click();
  };

  // Handle cover image file input change
  const handleCoverChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setCoverFile(file);
      
      // Create a preview URL
      const reader = new FileReader();
      reader.onloadend = () => {
        setCoverPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Trigger the file input click when clicking on the cover area
  const handleCoverClick = () => {
    coverFileInputRef.current.click();
  };

  // Handle form submission for community settings
  const handleGroupSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setSaving(true);
      setError('');
      
      if (!adminGroup || !adminGroup.id) {
        throw new Error('Community information not found');
      }
      
      // Create FormData object for consistent data handling
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('description', formData.description);
      formDataToSend.append('isPublic', formData.isPublic ? 'true' : 'false');
      
      // Add payment links as JSON string
      formDataToSend.append('paymentLinks', JSON.stringify(formData.paymentLinks));
      
      // Process logo file if selected
      if (logoFile) {
        try {
          // Convert file to base64 using a promise
          const logoBase64 = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = (error) => reject(error);
            reader.readAsDataURL(logoFile);
          });
          
          formDataToSend.append('logoFile', logoBase64);
        } catch (fileError) {
          console.error('Error processing logo file:', fileError);
          setError('Failed to process logo image. Please try a different file.');
          setSaving(false);
          return;
        }
      }

      // Process cover image file if selected
      if (coverFile) {
        try {
          // Convert file to base64 using a promise
          const coverBase64 = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = (error) => reject(error);
            reader.readAsDataURL(coverFile);
          });
          
          formDataToSend.append('coverFile', coverBase64);
        } catch (fileError) {
          console.error('Error processing cover image file:', fileError);
          setError('Failed to process cover image. Please try a different file.');
          setSaving(false);
          return;
        }
      }
      
      console.log('Submitting community update with data:', {
        name: formData.name,
        description: formData.description?.substring(0, 30) + '...',
        isPublic: formData.isPublic,
        hasLogo: !!logoFile,
        hasCover: !!coverFile,
        paymentLinks: formData.paymentLinks
      });
      
      // Use the context function to update the group
      await updateAdminGroup(adminGroup.id, formDataToSend);
      
      setSuccessMessage('Community settings updated successfully');
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
    } catch (err) {
      console.error('Error updating community settings:', err);
      if (err.response) {
        console.error('Server response:', err.response.status, err.response.data);
      }
      setError('Failed to update community settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Handle password change submission
  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setSaving(true);
      setError('');
      
      // Basic validation
      if (formData.newPassword !== formData.confirmPassword) {
        throw new Error('New password and confirmation do not match');
      }
      
      if (formData.newPassword.length < 6) {
        throw new Error('Password must be at least 6 characters long');
      }
      
      // Call the password update API (you'll need to create this endpoint)
      await authService.updatePassword({
        currentPassword: formData.currentPassword,
        newPassword: formData.newPassword
      });
      
      // Clear password fields
      setFormData(prevState => ({
        ...prevState,
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      }));
      
      setSuccessMessage('Password updated successfully');
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
    } catch (err) {
      console.error('Error changing password:', err);
      setError(err.response?.data?.message || err.message || 'Failed to update password. Please try again.');
    } finally {
      setSaving(false);
    }
  };



  // Handle logout
  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Handle back button
  const handleBack = () => {
    if (adminGroup && adminGroup.id) {
      navigate(`/group/${adminGroup.id}`);
    } else {
      navigate('/dashboard');
    }
  };

  // Show loading state
  if (groupLoading) {
    return (
      <div className="admin-profile-loading">
        <div className="loading-spinner"></div>
        <p>Loading profile information...</p>
      </div>
    );
  }

  return (
    <AdminLayout>
      <div className="admin-profile-container">
      {/* Header */}
      <div className="admin-profile-header">
        <button className="back-button" onClick={handleBack}>
          <FontAwesomeIcon icon={faArrowLeft} />
        </button>
        <h1>Profile Settings</h1>
      </div>

      {/* Error message */}
      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {/* Tabs */}
      <div className="profile-tabs">
        <button 
          className={`profile-tab ${activeTab === 'account' ? 'active' : ''}`} 
          onClick={() => setActiveTab('account')}
        >
          <FontAwesomeIcon icon={faUser} />
          Account
        </button>
        <button 
          className={`profile-tab ${activeTab === 'password' ? 'active' : ''}`} 
          onClick={() => setActiveTab('password')}
        >
          <FontAwesomeIcon icon={faLock} />
          Password
        </button>

      </div>

      {/* Account Tab */}
      {activeTab === 'account' && (
        <div className="account-tab-content">
          {/* Left Side - Community Settings */}
          <div className="community-settings-section">
            <h2>Community Settings</h2>
            <form onSubmit={handleGroupSubmit} className="settings-form">
          {/* Community Logo */}
          <div className="logo-upload-section">
            <div 
              className="logo-upload" 
              onClick={handleLogoClick}
              style={{ backgroundImage: logoPreview ? `url(${logoPreview})` : 'none' }}
            >
              {!logoPreview && <FontAwesomeIcon icon={faCamera} size="2x" />}
            </div>
            <input 
              type="file"
              ref={fileInputRef}
              onChange={handleLogoChange}
              accept="image/*"
              className="file-input"
            />
            <p className="logo-hint">Click to upload a new logo</p>
            <p className="logo-requirements">
              Recommended: 300×300px, max 5MB (JPEG, PNG, GIF)
            </p>
          </div>
          
          {/* Community Cover Image */}
          <div className="cover-upload-section">
            <h3>Cover Image</h3>
            <div 
              className="cover-upload" 
              onClick={handleCoverClick}
              style={{ backgroundImage: coverPreview ? `url(${coverPreview})` : 'none' }}
            >
              {!coverPreview && (
                <div className="cover-placeholder">
                  <FontAwesomeIcon icon={faCamera} size="2x" />
                  <span>Upload Cover Image</span>
                </div>
              )}
            </div>
            <input 
              type="file"
              ref={coverFileInputRef}
              onChange={handleCoverChange}
              accept="image/*"
              className="file-input"
            />
            <p className="cover-hint">Click to upload a cover image</p>
            <p className="cover-requirements">
              Recommended: 1200×400px, max 5MB (JPEG, PNG, GIF)
            </p>
          </div>
          
          {/* Community Information */}
          <div className="form-group">
            <label htmlFor="name">Community Name</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              placeholder="Enter community name"
            />
          </div>

          <div className="form-group">
            <label htmlFor="description">Community Description</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows="4"
              placeholder="Enter community description"
            ></textarea>
          </div>

          {/* Payment Links Section */}
          <div className="payment-links-section">
            <h2>Tzedakah Box Payment Options</h2>
            <p className="section-description">
              Configure payment methods for your virtual tzedakah box. Members will see these options when they want to make a donation to your community.
            </p>
            
            <div className="form-group">
              <label htmlFor="paypal">PayPal</label>
              <input
                type="text"
                id="paypal"
                name="paymentLinks.paypal"
                value={formData.paymentLinks.paypal}
                onChange={handleChange}
                placeholder="PayPal info (link, email, mobile, username)"
              />
            </div>

            <div className="form-group">
              <label htmlFor="zelle">Zelle</label>
              <input
                type="text"
                id="zelle"
                name="paymentLinks.zelle"
                value={formData.paymentLinks.zelle}
                onChange={handleChange}
                placeholder="Zelle info (link, email, mobile, username)"
              />
            </div>

            <div className="form-group">
              <label htmlFor="venmo">Venmo</label>
              <input
                type="text"
                id="venmo"
                name="paymentLinks.venmo"
                value={formData.paymentLinks.venmo}
                onChange={handleChange}
                placeholder="Venmo info (link, email, mobile, username)"
              />
            </div>

            <div className="form-group">
              <label htmlFor="cashapp">Cash App</label>
              <input
                type="text"
                id="cashapp"
                name="paymentLinks.cashapp"
                value={formData.paymentLinks.cashapp}
                onChange={handleChange}
                placeholder="Cash App info (link, email, mobile, username)"
              />
            </div>

            <div className="form-group">
              <label htmlFor="other">Other Payment</label>
              <input
                type="text"
                id="other"
                name="paymentLinks.other"
                value={formData.paymentLinks.other}
                onChange={handleChange}
                placeholder="Other payment info (link, email, mobile, username)"
              />
            </div>
          </div>

              <button 
                type="submit" 
                className="save-button"
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Community Settings'}
                {!saving && <FontAwesomeIcon icon={faSave} />}
              </button>
            </form>
          </div>

          {/* Right Side - Admin Profile */}
          <div className="admin-profile-section">
            <h2>Admin Profile</h2>
            <div className="profile-info">
              <div className="profile-avatar">
                <FontAwesomeIcon icon={faUser} size="3x" />
              </div>
              <div className="profile-details">
                <h3>{profile?.name || currentUser?.email}</h3>
                <p className="profile-email">{currentUser?.email}</p>
                <p className="profile-role">Community Administrator</p>
                {profile?.stripe_account_id && (
                  <p className="stripe-status">
                    <FontAwesomeIcon icon={faCreditCard} /> Stripe Connected
                  </p>
                )}
              </div>
            </div>
            
            <div className="profile-stats">
              <div className="stat-item">
                <span className="stat-label">Account Created</span>
                <span className="stat-value">
                  {currentUser?.created_at ? new Date(currentUser.created_at).toLocaleDateString() : 'N/A'}
                </span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Community Name</span>
                <span className="stat-value">{adminGroup?.name || 'Loading...'}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Account Status</span>
                <span className="stat-value active">Active</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Password Tab */}
      {activeTab === 'password' && (
        <div className="password-settings">
          <form onSubmit={handlePasswordSubmit} className="settings-form">
            <h2>Change Password</h2>
            
            <div className="form-group">
              <label htmlFor="currentPassword">Current Password</label>
              <input
                type="password"
                id="currentPassword"
                name="currentPassword"
                value={formData.currentPassword}
                onChange={handleChange}
                required
                placeholder="Enter current password"
              />
            </div>

            <div className="form-group">
              <label htmlFor="newPassword">New Password</label>
              <input
                type="password"
                id="newPassword"
                name="newPassword"
                value={formData.newPassword}
                onChange={handleChange}
                required
                placeholder="Enter new password"
              />
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm New Password</label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                required
                placeholder="Confirm new password"
              />
            </div>

            <button 
              type="submit" 
              className="change-password-button"
              disabled={saving}
            >
              {saving ? 'Updating...' : 'Update Password'}
              {!saving && <FontAwesomeIcon icon={faLock} />}
            </button>
          </form>

          <div className="logout-section">
            <button 
              type="button" 
              className="logout-button"
              onClick={handleLogout}
            >
              Log Out
              <FontAwesomeIcon icon={faSignOutAlt} />
            </button>
          </div>
        </div>
      )}



      {/* Payment Links Modal */}
      {showPaymentLinksModal && (
        <PaymentLinksModal 
          isOpen={showPaymentLinksModal}
          onRequestClose={() => setShowPaymentLinksModal(false)}
          paymentLinks={formData.paymentLinks}
          onPaymentLinksChange={(newLinks) => setFormData(prev => ({ ...prev, paymentLinks: newLinks }))}
        />
      )}

      {/* Success Toast - positioned at bottom */}
      {successMessage && (
        <div className="success-toast">
          <FontAwesomeIcon icon={faCheck} /> {successMessage}
        </div>
      )}
    </div>
    </AdminLayout>
  );
};

export default AdminProfilePage;